import { FunctionComponent, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faThumbTack, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { PinnableFact } from "@/pages/Private/redux/deepResearch/usePinnedResearchFacts";

import { SourcePills } from "./SourcePills";

interface ResearchFactRowProps {
	fact: PinnableFact;
	/** Row marker: the point number, a bullet dot, … */
	marker?: ReactNode;
	children: ReactNode;
	isPinned: boolean;
	isPinning: boolean;
	/** Hidden while a text selection is active, so only the selection CTA is offered. */
	isSuppressed?: boolean;
	portalContainer?: HTMLElement | null;
	onTogglePin: (fact: PinnableFact) => void;
	/**
	 * Opens the lead's Chat tab with the AI generate panel ready, so a pinned fact
	 * leads straight into writing the sequence. Omitted by hosts without a Chat
	 * view (the Profile Tracker detail view) or for a lead that has none — the
	 * pinned CTA then keeps its plain unpin behaviour.
	 */
	onRequestSequence?: () => void;
}

/**
 * One hoverable research fact: text, its source pills, and the pin CTA.
 *
 * The CTA is absolutely positioned **over** the row's bottom-right corner
 * rather than reserving a column for itself — reserving space is what made the
 * rows so tall in the mockup (PREC-3680 feedback: "it can go over the text").
 * A pinned row keeps the CTA visible so the state is readable without hovering.
 *
 * Once a fact is pinned the CTA changes job: "In sequence" takes the user to the
 * Chat tab to write the message, and a separate ✕ removes the pin. Where no Chat
 * view exists there is nowhere to go, so the CTA stays a plain unpin toggle.
 */
export const ResearchFactRow: FunctionComponent<ResearchFactRowProps> = ({
	fact,
	marker,
	children,
	isPinned,
	isPinning,
	isSuppressed,
	portalContainer,
	onTogglePin,
	onRequestSequence,
}) => {
	const { t } = useTranslation();
	const [isActive, setIsActive] = useState(false);

	// A pinned fact is already collected — clicking it again should move the user
	// forward into writing, not silently undo the pin.
	const jumpsToSequence = isPinned && !!onRequestSequence;

	const handleClick = useCallback(() => {
		if (isPinned && onRequestSequence) {
			onRequestSequence();

			return;
		}

		onTogglePin(fact);
	}, [fact, isPinned, onRequestSequence, onTogglePin]);

	const handleUnpin = useCallback(() => onTogglePin(fact), [fact, onTogglePin]);

	// Hover/focus is tracked in state rather than through a `group-hover:` utility:
	// `opacity-0` and `group-hover/fact:opacity-100` carry the same specificity, so
	// which one wins depends on their order in the utilities layer. State keeps that
	// out of the picture and gives keyboard users the same affordance.
	const isCtaVisible = (isActive || isPinned) && !isSuppressed;

	return (
		<div
			className={classNames(
				"relative -mx-2 rounded-lg px-2 py-1.5 transition-colors",
				isActive && "bg-background-light"
			)}
			data-fact-key={fact.factKey}
			data-testid={`research-fact-${fact.factKey}`}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node)) {
					setIsActive(false);
				}
			}}
			onFocus={() => setIsActive(true)}
			onMouseEnter={() => setIsActive(true)}
			onMouseLeave={() => setIsActive(false)}
		>
			<div className="flex items-start gap-2.5">
				{marker}
				<div className="min-w-0 flex-1">
					{children}
					{!!fact.sources?.length && (
						<div className="mt-1.5 flex flex-wrap items-center gap-1.5 pr-28">
							<SourcePills portalContainer={portalContainer} sources={fact.sources} />
						</div>
					)}
				</div>
			</div>

			<div
				className={classNames(
					"absolute bottom-1 right-1 flex items-center gap-1 transition-opacity",
					// Idle rows stay calm: the CTA only shows on hover/focus, or when the
					// fact is already pinned. It is never unmounted, so the row's height
					// never changes.
					isCtaVisible ? "opacity-100" : "pointer-events-none opacity-0"
				)}
				data-testid={`research-fact-cta-${fact.factKey}`}
			>
				<button
					aria-pressed={isPinned}
					className={classNames(
						"inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-semibold disabled:cursor-not-allowed",
						isPinned
							? "border-brand-light bg-brand-light text-white"
							: "border-brand-light bg-white text-brand-light hover:bg-light-blue"
					)}
					data-testid={`research-fact-pin-${fact.factKey}`}
					disabled={isPinning}
					title={
						jumpsToSequence
							? t("profileTracker.deepResearch.writeSequenceFromFact")
							: isPinned
								? t("profileTracker.deepResearch.pinnedForSequence")
								: t("profileTracker.deepResearch.pinForSequence")
					}
					type="button"
					onClick={handleClick}
				>
					<Icon className="h-3 w-3" icon={isPinned ? faCheck : faThumbTack} />
					{isPinned
						? t("profileTracker.deepResearch.pinnedForSequence")
						: t("profileTracker.deepResearch.pinForSequence")}
				</button>

				{/* With the main CTA now leading into the Chat tab, removing the pin needs
				    its own control — otherwise a pinned fact could no longer be unpinned
				    from the research it was pinned in. */}
				{jumpsToSequence && (
					<button
						aria-label={t("profileTracker.deepResearch.unpinFromSequence") || undefined}
						className="inline-flex cursor-pointer items-center rounded-lg border border-border-light bg-white px-1.5 py-1 text-[11px] text-text-meta hover:border-status-error hover:text-status-error disabled:cursor-not-allowed"
						data-testid={`research-fact-unpin-${fact.factKey}`}
						disabled={isPinning}
						title={t("profileTracker.deepResearch.unpinFromSequence") || undefined}
						type="button"
						onClick={handleUnpin}
					>
						<Icon className="h-3 w-3" icon={faXmark} />
					</button>
				)}
			</div>
		</div>
	);
};
